import { Controller } from "@hotwired/stimulus"
import { editorStore } from "../editor/store"

// Floating toolbar that appears above (or below, when there isn't room)
// the currently-selected object on mobile. Listens to editor:selection-rect
// events dispatched by editor_controller whenever the selected object's
// viewport rect changes (selection, move, resize, rotate).
export default class extends Controller {
  connect() {
    this._onRect = (e) => this.#update(e.detail?.rect, e.detail?.anchorOffsetTop ?? 0)
    this._onClear = () => this.#hide()
    window.addEventListener("editor:selection-rect", this._onRect)
    window.addEventListener("editor:selection-clear", this._onClear)
    this.#hide()
  }

  disconnect() {
    window.removeEventListener("editor:selection-rect", this._onRect)
    window.removeEventListener("editor:selection-clear", this._onClear)
  }

  rotate() {
    const id = editorStore.selectedId
    if (!id) return
    const obj = editorStore.objects.find(o => o.id === id)
    if (!obj) return
    const next = Math.round(((obj.rotationDeg ?? 0) + 15) % 360)
    editorStore.pushSnapshot()
    editorStore.updateObject(id, { rotationDeg: next }, { snapshot: false })
  }

  duplicate() {
    if (editorStore.selectedId) editorStore.duplicateObject(editorStore.selectedId)
  }

  remove() {
    if (editorStore.selectedId) editorStore.removeObject(editorStore.selectedId)
  }

  openProperties() {
    // Let the sheet open properties without going through editor_controller.
    this.dispatch("request-properties", { bubbles: true })
  }

  #update(rect, anchorOffsetTop = 0) {
    if (!rect) { this.#hide(); return }
    this.element.classList.remove("hidden")
    const tbRect = this.element.getBoundingClientRect()
    const tbHeight = tbRect.height || 44
    const tbWidth = tbRect.width || 180
    const margin = 10
    const edgePad = 8
    // Prefer above; clear the rotation anchor (anchorOffsetTop) so the
    // toolbar doesn't cover the rotate handle that sticks out of the
    // Transformer. If putting it above would collide with the viewport
    // top, fall back to below the object.
    const above = rect.top - anchorOffsetTop - tbHeight - margin
    let y = above > 60 ? above : rect.bottom + margin
    // Clamp Y so the toolbar can't render offscreen for tall objects.
    y = Math.max(60, Math.min(y, window.innerHeight - tbHeight - 60))
    // We position the toolbar's actual top-left (no CSS translate). The
    // desired left edge is (object-centre − half-toolbar-width); clamp so
    // both edges stay inside the viewport. When viewport is narrower than
    // the toolbar itself, pin to edgePad so at least the leading edge is
    // visible.
    const cx = rect.left + rect.width / 2
    const desiredLeft = cx - tbWidth / 2
    const maxLeft = Math.max(edgePad, window.innerWidth - tbWidth - edgePad)
    const x = Math.max(edgePad, Math.min(maxLeft, desiredLeft))
    this.element.style.top = `${y}px`
    this.element.style.left = `${x}px`
  }

  #hide() {
    this.element.classList.add("hidden")
  }
}
